import { useDocumentVisibility, useLocalStorage } from '@vueuse/core'
import { computed, onMounted, onUnmounted, readonly, ref, watch } from 'vue'
import type { AiQueueItem, AiQueueStatus, ProcessingPriority, StatusCounts } from '../types'
import { ACTIVE_STATUSES } from '../types'
import { useAiDocumentApi } from './useAiDocumentApi'

const AUTO_REFRESH_STORAGE_KEY = 'AiDocumentProcessor_autoRefresh'
const ACTIVE_POLL_MS = 3000
const IDLE_POLL_MS = 15000
const SEARCH_DEBOUNCE_MS = 300

/**
 * Queue manager composable for AI document processing.
 *
 * Server-driven: filters, search, pagination and status counts all come from
 * the queue endpoint. Polling is adaptive — fast while any visible item is
 * still moving through the pipeline, slow when idle, paused while the tab is
 * hidden — and every user action triggers an immediate refresh.
 */
export function useQueueManager() {
  const api = useAiDocumentApi()
  const visibility = useDocumentVisibility()

  // Queue state
  const queueItems = ref<AiQueueItem[]>([])
  const currentPage = ref(1)
  const perPage = ref(20)
  const totalItems = ref(0)
  const lastPage = ref(1)
  const statusCounts = ref<StatusCounts | null>(null)

  // Filters (server-side)
  const statusFilter = ref<AiQueueStatus | 'all'>('all')
  const priorityFilter = ref<ProcessingPriority | 'all'>('all')
  const searchQuery = ref('')

  // Polling
  const autoRefresh = useLocalStorage(AUTO_REFRESH_STORAGE_KEY, true)
  const isRefreshing = ref(false)
  let pollTimer: ReturnType<typeof setTimeout> | null = null
  let searchTimer: ReturnType<typeof setTimeout> | null = null

  const isLoading = computed(() => api.loading.value || isRefreshing.value)

  const hasActiveFilters = computed(() => statusFilter.value !== 'all' || priorityFilter.value !== 'all' || searchQuery.value.trim() !== '')

  const hasActiveItems = computed(() => queueItems.value.some((item) => ACTIVE_STATUSES.includes(item.status)))

  /**
   * Load queue items using the current filters
   */
  const loadQueue = async (page = currentPage.value, pageSize = perPage.value): Promise<void> => {
    try {
      isRefreshing.value = true

      const response = await api.getQueue({
        page,
        perPage: pageSize,
        status: statusFilter.value,
        priority: priorityFilter.value,
        search: searchQuery.value,
      })

      if (response) {
        queueItems.value = response.data
        currentPage.value = response.current_page
        lastPage.value = response.last_page
        totalItems.value = response.total
        perPage.value = response.per_page
        if (response.status_counts) {
          statusCounts.value = response.status_counts
        }
      }
    } finally {
      isRefreshing.value = false
    }
  }

  const refresh = async (): Promise<void> => {
    await loadQueue(currentPage.value, perPage.value)
  }

  /**
   * Fetch a single item (with stages) and sync it into the loaded page
   */
  const getQueueItem = async (queueId: string): Promise<AiQueueItem | null> => {
    const response = await api.getQueueStatus(queueId)

    if (response) {
      const index = queueItems.value.findIndex((item) => item.id === queueId)
      if (index !== -1) {
        queueItems.value[index] = response
      }
    }

    return response
  }

  // ── Filters ────────────────────────────────────────────────────────────

  const setStatusFilter = async (status: AiQueueStatus | 'all'): Promise<void> => {
    statusFilter.value = status
    await loadQueue(1, perPage.value)
  }

  const setPriorityFilter = async (priority: ProcessingPriority | 'all'): Promise<void> => {
    priorityFilter.value = priority
    await loadQueue(1, perPage.value)
  }

  const setSearchQuery = (query: string): void => {
    searchQuery.value = query
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      void loadQueue(1, perPage.value)
    }, SEARCH_DEBOUNCE_MS)
  }

  const clearFilters = async (): Promise<void> => {
    statusFilter.value = 'all'
    priorityFilter.value = 'all'
    searchQuery.value = ''
    await loadQueue(1, perPage.value)
  }

  // ── Item actions (refresh after every mutation) ────────────────────────

  const runAndRefresh = async (action: () => Promise<boolean>): Promise<boolean> => {
    const success = await action()
    if (success) {
      await refresh()
    }
    return success
  }

  const cancelProcessing = (queueId: string): Promise<boolean> => runAndRefresh(() => api.cancelProcessing(queueId))

  const retryProcessing = (queueId: string): Promise<boolean> => runAndRefresh(() => api.retryProcessing(queueId))

  const deleteQueue = (queueId: string): Promise<boolean> => runAndRefresh(() => api.deleteQueue(queueId))

  const confirmModelType = (queueId: string, modelType: string): Promise<boolean> => runAndRefresh(() => api.confirmModelType(queueId, modelType))

  const bulkCancel = (queueIds: string[]): Promise<boolean> => runAndRefresh(() => api.bulkCancel(queueIds))

  const bulkRetry = (queueIds: string[]): Promise<boolean> => runAndRefresh(() => api.bulkRetry(queueIds))

  const bulkDelete = (queueIds: string[]): Promise<boolean> =>
    runAndRefresh(async () => {
      const results = await Promise.all(queueIds.map((queueId) => api.deleteQueue(queueId)))
      return results.every(Boolean)
    })

  // ── Adaptive polling ───────────────────────────────────────────────────

  const scheduleNextPoll = (): void => {
    if (pollTimer) clearTimeout(pollTimer)
    pollTimer = setTimeout(
      async () => {
        if (autoRefresh.value && visibility.value === 'visible' && !isLoading.value) {
          await refresh()
        }
        scheduleNextPoll()
      },
      hasActiveItems.value ? ACTIVE_POLL_MS : IDLE_POLL_MS,
    )
  }

  const stopPolling = (): void => {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  const toggleAutoRefresh = (): void => {
    autoRefresh.value = !autoRefresh.value
    if (autoRefresh.value) {
      void refresh()
      scheduleNextPoll()
    } else {
      stopPolling()
    }
  }

  // Catch up as soon as the tab becomes visible again
  watch(visibility, (current) => {
    if (current === 'visible' && autoRefresh.value) {
      void refresh()
      scheduleNextPoll()
    }
  })

  onMounted(async () => {
    await loadQueue()
    if (autoRefresh.value) {
      scheduleNextPoll()
    }
  })

  onUnmounted(() => {
    stopPolling()
    if (searchTimer) clearTimeout(searchTimer)
  })

  return {
    // State (readonly)
    queueItems: readonly(queueItems),
    currentPage: readonly(currentPage),
    perPage: readonly(perPage),
    totalItems: readonly(totalItems),
    lastPage: readonly(lastPage),
    statusCounts: readonly(statusCounts),
    statusFilter: readonly(statusFilter),
    priorityFilter: readonly(priorityFilter),
    searchQuery: readonly(searchQuery),
    autoRefresh: readonly(autoRefresh),
    hasActiveFilters,
    hasActiveItems,
    isLoading,
    apiError: api.error,

    // Actions
    loadQueue,
    refresh,
    getQueueItem,
    setStatusFilter,
    setPriorityFilter,
    setSearchQuery,
    clearFilters,
    cancelProcessing,
    retryProcessing,
    deleteQueue,
    confirmModelType,
    bulkCancel,
    bulkRetry,
    bulkDelete,
    toggleAutoRefresh,

    // API actions
    clearApiError: api.clearError,
  } as const
}

export type QueueManagerContext = ReturnType<typeof useQueueManager>
